/**
 * Itinerary Repository
 *
 * Data access layer for Itinerary entity.
 * Abstracts API calls and provides domain-specific interface.
 */
import { itineraryApi } from "@infrastructure/api";
import { normalizeItineraryResponse, prepareBookingItemsForApi, } from "@/utils/itinerary-utils";
import { mapItineraryPdfProjection } from "../services/itinerary-pdf-projection.mapper";
import { processPdfJobStream } from "../services/itinerary-pdf-stream.service";
// =============================================================================
// IMPLEMENTATION
// =============================================================================
export const itineraryRepository = {
    async getAll() {
        try {
            const response = await itineraryApi.getItineraries();
            const data = response.data;
            if (!Array.isArray(data)) {
                console.warn("[itineraryRepository] getAll: response.data is not an array");
                return [];
            }
            // Normalize each itinerary for backward compatibility
            return data.map((itinerary) => normalizeItineraryResponse(itinerary));
        }
        catch (error) {
            console.error("[itineraryRepository] Error loading itineraries:", error);
            throw error;
        }
    },
    async getProviderBookings() {
        try {
            const response = await itineraryApi.getProviderBookings();
            const data = response.data;
            if (!Array.isArray(data)) {
                console.warn("[itineraryRepository] getProviderBookings: response.data is not an array");
                return [];
            }
            return data.map((itinerary) => normalizeItineraryResponse(itinerary));
        }
        catch (error) {
            console.error("[itineraryRepository] Error loading provider bookings:", error);
            throw error;
        }
    },
    async getById(id) {
        try {
            const response = await itineraryApi.getById(id);
            return normalizeItineraryResponse(response.data);
        }
        catch (error) {
            console.error(`[itineraryRepository] Error loading itinerary ${id}:`, error);
            throw error;
        }
    },
    async getPdfProjection(id) {
        try {
            const response = await itineraryApi.getPdfProjection(id);
            return mapItineraryPdfProjection(response.data);
        }
        catch (error) {
            console.error(`[itineraryRepository] Error loading itinerary PDF projection ${id}:`, error);
            throw error;
        }
    },
    async downloadPdf(id) {
        try {
            const response = await itineraryApi.downloadPdf(id);
            return response.data;
        }
        catch (error) {
            console.error(`[itineraryRepository] Error downloading itinerary PDF ${id}:`, error);
            throw error;
        }
    },
    async streamPdfJobEvents(id, jobId, callbacks, options = {}) {
        try {
            const response = await itineraryApi.streamPdfJobEvents(id, jobId, options);
            const body = response.data;
            if (!body) {
                throw new Error("Response body is null");
            }
            await processPdfJobStream(body, callbacks);
        }
        catch (error) {
            console.error(`[itineraryRepository] Error streaming PDF job events for itinerary ${id}:`, error);
            throw error;
        }
    },
    async create(data) {
        try {
            // quantity_of_participants is now per-booking-item, not global
            const payload = {
                customer_id: data.customer_id,
                booking_items: prepareBookingItemsForApi(data.booking_items),
                tags: data.tags || [],
            };
            const response = await itineraryApi.save(payload);
            return normalizeItineraryResponse(response.data);
        }
        catch (error) {
            console.error("[itineraryRepository] Error creating itinerary:", error);
            throw error;
        }
    },
    async update(id, data) {
        try {
            const payload = {};
            if (data.customer_id !== undefined)
                payload.customer_id = data.customer_id;
            if (data.tags !== undefined)
                payload.tags = data.tags;
            const response = await itineraryApi.update(id, payload);
            return normalizeItineraryResponse(response.data);
        }
        catch (error) {
            console.error(`[itineraryRepository] Error updating itinerary ${id}:`, error);
            throw error;
        }
    },
    async delete(id) {
        try {
            await itineraryApi.delete(id);
        }
        catch (error) {
            console.error(`[itineraryRepository] Error deleting itinerary ${id}:`, error);
            throw error;
        }
    },
    async updateState(id, state) {
        try {
            const response = await itineraryApi.updateState(id, {
                state,
            });
            return normalizeItineraryResponse(response.data);
        }
        catch (error) {
            console.error(`[itineraryRepository] Error updating status for itinerary ${id}:`, error);
            throw error;
        }
    },
    async updateStatus(id, state) {
        return this.updateState(id, state);
    },
};
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default itineraryRepository;
